import { LettureOdoo } from "../utils/lettureOdoo";
import type { RiepilogoUtentiConti } from "./utentiCassaforte";
import { apiClient } from "./client";
import type { TipoDispositivoDevice, TipoServizioPrecredit } from "./devices";

export interface Cliente {
  id: string;
  codice_cliente: string | null;
  codice_g: string | null;
  nome: string;
  insegna: string | null;
  tipo_contatto: "cliente" | "fornitore" | "altro";
  codice_ateco: string | null;
  descrizione_attivita: string | null;
  numero_rea: string | null;
  registro_imprese: string | null;
  /** Come la scrive la visura camerale: "impresa individuale", "s.r.l."… */
  forma_giuridica: string | null;
  partita_iva: string | null;
  codice_sdi: string | null;
  pec_fatturazione: string | null;
  regione: string | null;
  comune: string | null;
  cap: string | null;
  provincia: string | null;
  indirizzo: string | null;
  civico: string | null;
  pec: string | null;
  email: string | null;
  telefono: string | null;
  referente_nome: string | null;
  referente_email: string | null;
  referente_telefono: string | null;
  referente_2_nome: string | null;
  rappresentante_nome: string | null;
  rappresentante_qualita: string | null;
  rappresentante_telefono: string | null;
  gestore_accentrato: string | null;
  vettore_versamento: string | null;
  sala_conta_versamento: string | null;
  email_ritiro: string | null;
  data_contratto: string | null;
  n_bcn: string | null;
  // Decimal Prisma -> stringa in JSON, stesso motivo dei costi in Installation.
  valore: string | null;
  iban_accredito: string | null;
  banca: string | null;
  vecchio_iban: string | null;
  cod_g_vecchio: string | null;
  note: string | null;
  odoo_partner_id: number | null;
  odoo_synced: boolean;
  odoo_last_sync: string | null;
  _count?: { sedi: number };
}

/** Come sono state ottenute le coordinate di una sede. Rispecchia l'enum Prisma. */
export type GeoPrecisione = "indirizzo" | "comune" | "manuale";

export interface SedeCliente {
  id: string;
  cliente_id: string;
  nome_sede: string | null;
  indirizzo: string | null;
  citta: string | null;
  cap: string | null;
  provincia: string | null;
  email: string | null;
  telefono: string | null;
  note: string | null;
  /**
   * Coordinate bancarie DI QUESTO punto vendita. Nulle sulla quasi totalità
   * delle sedi, ed è il caso normale: significa "usa quelle dell'azienda".
   * Chi deve sapere quale IBAN vale davvero non le legge da qui ma usa
   * `accredito` (vedi `AttivitaDiSede`), che applica l'ereditarietà.
   */
  iban_accredito: string | null;
  banca_accredito: string | null;
  agenzia_accredito: string | null;
  /**
   * Come accredita la sede (`iban` | `conto_interno`, o null) e da chi è stato
   * scritto per ultimo. Arrivano già col `findMany` senza `select`: dichiararli
   * serve al form per avvisare che la sede accredita su un conto interno.
   */
  accredito_tipo?: string | null;
  accredito_conto_interno?: string | null;
  accredito_origine?: "aznext" | "excel" | "manuale" | null;
  /**
   * Coordinate e provenienza Odoo. Stavano solo su `SedeConPosizione` (la
   * scheda Sede), ma il backend le manda GIA' su ogni elenco di sedi: la rotta
   * fa `findMany` senza `select`, quindi tutte le colonne scalari sono nel
   * payload. Il tipo diceva meno di quello che arrivava, e la tabella non
   * poteva offrire le colonne "Posizionata" e "Da Odoo" pur avendo il dato.
   *
   * Dichiararle qui non costa una query in piu' ne' un campo in piu' sulla
   * rete: cambia solo cosa TypeScript sa di poter leggere.
   */
  lat: string | number | null;
  lng: string | number | null;
  geo_precisione: GeoPrecisione | null;
  /** Presente = la sede viene dalla sincronizzazione Odoo, non creata a mano. */
  odoo_partner_id: number | null;
  /** Solo dall'elenco delle sedi di un cliente: utenze, conti tecnici, accredito e avviso. */
  utenti_conti?: RiepilogoUtentiConti;
}

// ---------------------------------------------------------------------------
// Vista Sede: tutto quello che è successo, e sta per succedere, presso un
// singolo punto vendita. Un livello sotto la vista aggregata per cliente.
// ---------------------------------------------------------------------------

/**
 * Sede con l'azienda risolta. Coordinate e `geo_precisione` vivono ora sulla
 * base `SedeCliente` — `geo_precisione` nulla significa "mai geocodificata", e
 * `manuale` significa inserita da una persona dalla scheda Sede: precisa quanto
 * `indirizzo`, ma distinguibile da quella prodotta dal geocoding automatico.
 */
export interface SedeConPosizione extends SedeCliente {
  // `iban_accredito`/`banca` dell'azienda arrivano insieme alla sede perché
  // sono la fonte da cui si eredita quando la sede non ha coordinate proprie.
  // Il risultato già risolto è `accredito` su AttivitaDiSede: questi due
  // servono solo a poter dire DA DOVE viene, quando è ereditato.
  cliente: { id: string; nome: string; iban_accredito: string | null; banca: string | null } | null;
}

/**
 * Quale IBAN vale davvero per questa sede, già risolto dal backend
 * (`utils/ibanSede.ts`). `sovrascritto = false` significa che la sede non ha
 * coordinate proprie e sta usando quelle dell'azienda — non che manchino.
 *
 * Risolto lì e non qui perché la stessa regola servirà al mail-merge del
 * contratto: scritta una volta sola non può divergere fra i due usi.
 */
export interface AccreditoEffettivo {
  /** Sempre null quando la sede accredita su un conto interno. */
  iban: string | null;
  /** true = la sede accredita su un conto interno e non eredita l'IBAN dell'azienda. */
  su_conto_interno: boolean;
  conto_interno: string | null;
  beneficiario: string | null;
  banca: string | null;
  agenzia: string | null;
  sovrascritto: boolean;
}

export interface InstallazioneDiSede {
  id: string;
  insegna: string | null;
  localita: string | null;
  ipotesi_data: string | null;
  data_effettiva: string | null;
  stato: string | null;
  installatore: string | null;
}

export interface InterventoDiSede {
  id: string;
  nome: string | null;
  cliente_nome: string | null;
  data_prevista: string | null;
  data_completamento: string | null;
  stato: string | null;
  tecnico: string | null;
}

export interface SopralluogoDiSede {
  id: string;
  ragione_sociale_cliente: string | null;
  insegna_cliente: string | null;
  data_sopralluogo: string | null;
  stato: string | null;
}

export interface AttivazioneDiSede {
  id: string;
  cliente_nome: string | null;
  data_attivazione: string | null;
  data_effettiva: string | null;
  stato: string | null;
  tecnico_attivazione: string | null;
}

/**
 * Un device attualmente collocato presso questa sede. Arriva già appiattito
 * dal backend: la tabella ponte `device_cliente` resta un dettaglio del
 * server, qui si vede il pezzo fisico con il codice GDO della collocazione.
 */
export interface DispositivoDiSede {
  id: string;
  matricola: string | null;
  stato: "attivo" | "dismesso" | "rubato";
  /** null = non classificato. */
  tipo_dispositivo: TipoDispositivoDevice | null;
  data_installazione: string | null;
  modello_raw: string | null;
  dispositivo_catalogo: { id: string; marca: string; modello: string } | null;
  codice_gdo: string | null;
  /** Il servizio della relazione con questa sede: quello che prende una pratica avviata senza indicarlo (PR E1). */
  servizio_precredit: TipoServizioPrecredit | null;
  /** Il cliente della sede ha già un'attivazione fatta su questo dispositivo: «Sede già attiva» si propone spuntata. */
  attivazione_effettuata: boolean;
}

export interface AttivitaDiSede {
  sede: SedeConPosizione;
  accredito: AccreditoEffettivo;
  installazioni: InstallazioneDiSede[];
  dispositivi: DispositivoDiSede[];
  interventi: InterventoDiSede[];
  sopralluoghi: SopralluogoDiSede[];
  attivazioni: AttivazioneDiSede[];
}

export async function getAttivitaSede(sedeId: string): Promise<AttivitaDiSede> {
  const { data } = await apiClient.get<AttivitaDiSede>(`/sedi/${sedeId}/attivita`);
  return data;
}

export async function listClienti(search?: string): Promise<{ clienti: Cliente[] }> {
  const { data } = await apiClient.get<{ clienti: Cliente[] }>("/clienti", { params: { search } });
  return data;
}

/** Un cliente per id: serve alla scheda `/clients/:id` aperta da un link. */
export async function getCliente(id: string): Promise<{ cliente: Cliente }> {
  const { data } = await apiClient.get<{ cliente: Cliente }>(`/clienti/${id}`);
  return data;
}

// Vista aggregata "tutto quello che riguarda questo cliente" (via cliente_id).
export interface ClienteAttivita {
  installazioni: { id: string; insegna: string | null; localita: string | null; ipotesi_data: string | null; data_effettiva: string | null; stato: string }[];
  sopralluoghi: { id: string; ragione_sociale_cliente: string | null; insegna_cliente: string | null; data_sopralluogo: string | null; stato: string }[];
  interventi: { id: string; nome: string | null; cliente_nome: string | null; data_prevista: string | null; data_completamento: string | null; stato: string | null }[];
  attivazioni: { id: string; cliente_nome: string | null; sede: string | null; data_attivazione: string | null; data_effettiva: string | null; stato: string }[];
}

export async function getClienteAttivita(id: string): Promise<ClienteAttivita> {
  const { data } = await apiClient.get<ClienteAttivita>(`/clienti/${id}/attivita`);
  return data;
}

export async function createCliente(payload: Partial<Cliente>): Promise<{ cliente: Cliente }> {
  const { data } = await apiClient.post<{ cliente: Cliente }>("/clienti", payload);
  return data;
}

export async function updateCliente(id: string, payload: Partial<Cliente>): Promise<{ cliente: Cliente }> {
  const { data } = await apiClient.patch<{ cliente: Cliente }>(`/clienti/${id}`, payload);
  return data;
}

export async function listSediByCliente(clienteId: string): Promise<{ sedi: SedeCliente[] }> {
  const { data } = await apiClient.get<{ sedi: SedeCliente[] }>(`/clienti/${clienteId}/sedi`);
  return data;
}

export async function createSede(payload: Partial<SedeCliente>): Promise<{ sede: SedeCliente }> {
  const { data } = await apiClient.post<{ sede: SedeCliente }>("/sedi", payload);
  return data;
}

export async function updateSede(id: string, payload: Partial<SedeCliente>): Promise<{ sede: SedeCliente }> {
  const { data } = await apiClient.patch<{ sede: SedeCliente }>(`/sedi/${id}`, payload);
  return data;
}

export async function deleteSede(id: string): Promise<{ success: true }> {
  const { data } = await apiClient.delete<{ success: true }>(`/sedi/${id}`);
  return data;
}

/**
 * Coordinate inserite a mano. Endpoint dedicato e non la PATCH generica: là si
 * potrebbero salvare lat/lng lasciando `geo_precisione` a quello che era, e la
 * sede direbbe di essere stata geocodificata quando non lo è stata. I quattro
 * campi (lat, lng, precisione, data) si scrivono insieme lato server.
 */
export async function salvaPosizioneManuale(
  id: string,
  lat: string,
  lng: string
): Promise<{ sede: SedeConPosizione }> {
  // `SedeConPosizione` e non `SedeCliente`: la rotta restituisce la riga
  // aggiornata per intero, coordinate e precisione comprese — che sono
  // esattamente i campi per cui la si è chiamata.
  const { data } = await apiClient.patch<{ sede: SedeConPosizione }>(`/sedi/${id}/posizione`, { lat, lng });
  return data;
}

/** Sposta una sede sotto un'altra azienda madre — azione dedicata, separata dall'update generico. */
export async function spostaSede(id: string, nuovoClienteId: string): Promise<{ sede: SedeCliente }> {
  const { data } = await apiClient.patch<{ sede: SedeCliente }>(`/sedi/${id}/sposta`, { nuovo_cliente_id: nuovoClienteId });
  return data;
}

/** Legge i contatti-azienda da Odoo — le aziende madri diventano clienti, i punti vendita figli diventano sedi. */
export async function pullClientiFromOdoo(): Promise<{
  synced: number;
  sedi_synced: number;
  sedi_senza_genitore: number;
  doppioni_residui: { odoo_partner_id: number; nome: string; cliente_id: string }[];
  /**
   * Rumore che arriva da Odoo e va ripulito LA'. Niente `cliente_id`, a
   * differenza dei doppioni qui sopra: su queste due categorie l'app non offre
   * un'azione, perche' una cancellazione locale verrebbe disfatta dalla
   * sincronizzazione successiva.
   */
  contatti_prova: { odoo_partner_id: number; nome: string }[];
  gusci_vuoti: { odoo_partner_id: number; nome: string }[];
  /** Quanto era piena la lettura da Odoo — vedi utils/lettureOdoo.ts. */
  lettura?: LettureOdoo;
}> {
  const { data } = await apiClient.post("/clienti/sync/pull");
  return data;
}

/** Crea o aggiorna il res.partner corrispondente a questo cliente. */
export async function pushClienteToOdoo(id: string): Promise<{ cliente: Cliente }> {
  const { data } = await apiClient.post<{ cliente: Cliente }>(`/clienti/${id}/sync/push`);
  return data;
}

export async function deleteCliente(id: string): Promise<{ success: true }> {
  const { data } = await apiClient.delete<{ success: true }>(`/clienti/${id}`);
  return data;
}

/** Crea un vero Centro Assistenza a partire dai dati di questo cliente — non tocca né elimina il Cliente originale. */
export async function convertiInCentroAssistenza(id: string): Promise<{ centro: { id: string; nome: string } }> {
  const { data } = await apiClient.post(`/clienti/${id}/converti-centro-assistenza`);
  return data;
}

/** Crea un vero Istituto di Vigilanza a partire dai dati di questo cliente — non tocca né elimina il Cliente originale. */
export async function convertiInIstitutoVigilanza(id: string): Promise<{ istituto: { id: string; nome: string } }> {
  const { data } = await apiClient.post(`/clienti/${id}/converti-istituto-vigilanza`);
  return data;
}

// ---------------------------------------------------------------------------
// Documenti del cliente — il fascicolo camerale (visura e simili).
//
// Distinti dai media (`api/media.ts`), che restano foto e video del negozio:
// qui contano il TIPO documentale e la DATA del documento, che un allegato
// generico non ha. I due riquadri convivono sulla scheda cliente e dicono cose
// diverse.
// ---------------------------------------------------------------------------

/**
 * Che cosa cambierebbe una Scheda Cliente, senza che cambi niente.
 *
 * Il calcolo lo fa `applicaSchedaCliente` in modalità sola anteprima: le
 * stesse righe che poi scrivono davvero, quindi quello che si legge qui è
 * quello che si otterrà confermando.
 */
export interface ClienteCandidatoScheda {
  id: string;
  nome: string;
  insegna: string | null;
  partita_iva: string | null;
  sedi: { nome_sede: string | null; citta: string | null }[];
  numero_sedi: number;
}

export interface EsitoAnteprimaScheda {
  cliente: { id: string; nome: string; insegna: string | null; partita_iva: string | null } | null;
  /** La P.IVA letta dal file: serve a capire perché un cliente non si trova. */
  partita_iva_letta: string | null;
  esito: EsitoScheda | null;
  errore: string | null;
  /**
   * Più clienti con la stessa Partita IVA — succede davvero e non è un errore
   * (in produzione un gruppo e il suo cliente «storico» la condividono). Si
   * sceglie a mano e si richiama l'anteprima con quel `clienteId`.
   */
  candidati: ClienteCandidatoScheda[] | null;
}

/**
 * Legge la Scheda e dice cosa farebbe. Non scrive niente.
 *
 * Con `clienteId` il cliente è quello (si arriva dalla sua pagina); senza, lo
 * si cerca dalla Partita IVA scritta nel file. La conferma passa poi da
 * `uploadDocumentoCliente`, che è l'unico punto che scrive e archivia.
 */
export async function anteprimaSchedaCliente(
  file: File,
  clienteId?: string,
  scelteSedi?: Record<string, string>
): Promise<EsitoAnteprimaScheda> {
  const form = new FormData();
  form.append("file", file);
  if (clienteId) form.append("cliente_id", clienteId);
  // Le scelte sui punti vendita ambigui viaggiano come JSON dentro un campo:
  // il corpo è multipart, non JSON.
  if (scelteSedi && Object.keys(scelteSedi).length > 0) {
    form.append("scelte_sedi", JSON.stringify(scelteSedi));
  }
  // L'header va forzato: `apiClient` nasce con `Content-Type: application/json`
  // (vedi api/client.ts), e una FormData spedita così arriva al backend senza
  // boundary — multer non vede nessun file e la rotta risponde «Nessun file
  // caricato». Stessa riga che ha `uploadDocumentoCliente` qui sotto, per la
  // stessa ragione.
  const { data } = await apiClient.post<EsitoAnteprimaScheda>("/scheda-cliente/anteprima", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export type TipoDocumentoCliente = "visura_camerale" | "scheda_cliente_compilata" | "altro";

/**
 * Il formato ammesso PER TIPO, e non uno solo per tutti.
 *
 * La Scheda Cliente va caricata nel suo `.xlsx` originale perché il backend la
 * **legge**: da un PDF i campi non si estraggono. Gli altri documenti restano
 * PDF, che è la forma in cui arrivano. Il backend applica la stessa regola e
 * rifiuta la coppia sbagliata con un 400 — questa costante serve a non far
 * nemmeno scegliere un file che verrebbe respinto.
 */
export const MIME_PER_TIPO: Record<TipoDocumentoCliente, string> = {
  visura_camerale: "application/pdf",
  scheda_cliente_compilata: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  altro: "application/pdf",
};

/**
 * Il nome leggibile di un campo, per il resoconto della Scheda.
 *
 * Il backend manda i nomi delle COLONNE — `pec_fatturazione`,
 * `rappresentante_codice_fiscale` — che a chi carica il modulo non dicono
 * granché. Qui si traducono.
 *
 * Copre tutti i campi che `applicaSchedaCliente` può segnalare, a livello di
 * cliente e di sede. **La ricerca ha sempre un ripiego sul nome tecnico**
 * (vedi `etichettaCampo`): una colonna aggiunta domani e dimenticata qui esce
 * col suo nome grezzo — brutto ma vero — invece di sparire o rendere `undefined`.
 */
const ETICHETTA_CAMPO: Record<string, string> = {
  // Anagrafica
  insegna: "Insegna",
  numero_rea: "N. REA",
  codice_ateco: "Codice ATECO",
  descrizione_attivita: "Tipo di attività",
  indirizzo: "Indirizzo",
  civico: "Civico",
  comune: "Comune",
  citta: "Comune",
  cap: "CAP",
  provincia: "Provincia",
  telefono: "Telefono",
  email: "E-mail",
  pec: "PEC",
  // Fatturazione
  codice_sdi: "Codice SDI",
  pec_fatturazione: "PEC di fatturazione",
  email_fatture_cortesia: "E-mail per fatture di cortesia",
  partita_iva: "Partita IVA",
  codice_fiscale: "Codice fiscale",
  // Coordinate bancarie
  iban_accredito: "IBAN di accredito",
  banca_accredito: "Banca di accredito",
  agenzia_accredito: "Agenzia",
  // Legale rappresentante
  rappresentante_nome: "Legale rappresentante — nome",
  rappresentante_qualita: "Legale rappresentante — qualità",
  rappresentante_telefono: "Legale rappresentante — telefono",
  rappresentante_email: "Legale rappresentante — e-mail",
  rappresentante_codice_fiscale: "Legale rappresentante — codice fiscale",
  // Referente
  referente_nome: "Referente — nome",
  referente_qualita: "Referente — qualità",
  referente_telefono: "Referente — telefono",
  referente_email: "Referente — e-mail",
  // Altro
  note: "Note",
};

/**
 * L'etichetta di un campo, col nome tecnico come ripiego.
 *
 * Il ripiego non è pigrizia: preferire un nome brutto a uno assente. Se domani
 * il backend segnala un campo che questa mappa non conosce, l'utente lo vede
 * comunque e può chiedere cos'è — mentre una stringa vuota gli nasconderebbe
 * che un valore del modulo è stato scartato.
 */
export function etichettaCampo(campo: string): string {
  return ETICHETTA_CAMPO[campo] ?? campo;
}

/** Un campo che il modulo dice diverso da quanto c'è a sistema, e che NON è stato scritto. */
export interface CampoSaltato {
  campo: string;
  attuale: string;
  nel_modulo: string;
}

export interface SedeCandidataScheda {
  sede_id: string;
  nome_sede: string | null;
  citta: string | null;
  indirizzo: string | null;
  /** «insegna», «indirizzo», o entrambe quando i due criteri concordano. */
  trovata_da: string;
}

export interface EsitoSedeScheda {
  foglio: string;
  insegna: string | null;
  /** `null` quando nessuna sede combacia, o quando la scelta è ancora aperta. */
  sede_id: string | null;
  campi_riempiti: string[];
  campi_saltati: CampoSaltato[];
  /** Più sedi possibili: finché non se ne sceglie una, il foglio non si scrive. */
  candidate: SedeCandidataScheda[] | null;
}

/**
 * Il resoconto della lettura, quando il documento è una Scheda Cliente.
 *
 * Va MOSTRATO e non solo ricevuto: la regola del backend è «riempi solo ciò che
 * è vuoto», quindi senza questo resoconto chi carica non ha modo di sapere che
 * il modulo diceva una PEC diversa e che si è tenuta quella a sistema.
 */
export interface EsitoScheda {
  applicato: boolean;
  campi_cliente: string[];
  campi_saltati: CampoSaltato[];
  sedi: EsitoSedeScheda[];
  avvisi: string[];
}

export interface DocumentoCliente {
  id: string;
  cliente_id: string;
  tipo: TipoDocumentoCliente;
  file_url: string;
  nome_file: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  /** Data di emissione stampata sul documento, non quella di caricamento. */
  data_documento: string | null;
  created_at: string;
  creatore: { id: string; full_name: string | null; email: string } | null;
}

export async function listDocumentiCliente(clienteId: string): Promise<{ documenti: DocumentoCliente[] }> {
  const { data } = await apiClient.get<{ documenti: DocumentoCliente[] }>("/documenti-cliente", {
    params: { cliente_id: clienteId },
  });
  return data;
}

export async function uploadDocumentoCliente(
  clienteId: string,
  file: File,
  tipo: TipoDocumentoCliente,
  dataDocumento?: string,
  /** Solo per una Scheda: le scelte fatte in anteprima sui punti vendita ambigui. */
  scelteSedi?: Record<string, string>
): Promise<{ documento: DocumentoCliente; scheda: EsitoScheda | null }> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("cliente_id", clienteId);
  formData.append("tipo", tipo);
  if (scelteSedi && Object.keys(scelteSedi).length > 0) {
    formData.append("scelte_sedi", JSON.stringify(scelteSedi));
  }
  // Solo se valorizzata: un campo vuoto arriverebbe come stringa "" e il
  // backend la rifiuterebbe come data non valida, invece di trattarla come
  // "non indicata".
  if (dataDocumento) formData.append("data_documento", dataDocumento);

  // `scheda` torna valorizzata SOLO per una Scheda Cliente: per gli altri tipi
  // il backend manda `null`, perché non c'è niente da leggere.
  const { data } = await apiClient.post<{ documento: DocumentoCliente; scheda: EsitoScheda | null }>(
    "/documenti-cliente",
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data;
}

export async function deleteDocumentoCliente(id: string): Promise<{ success: true }> {
  const { data } = await apiClient.delete<{ success: true }>(`/documenti-cliente/${id}`);
  return data;
}

// ---------------------------------------------------------------------------
// Operatori autorizzati al versamento — l'elenco per SEDE delle persone che
// possono versare nella cassaforte. È la lista che il contratto di Cash
// Management (art. 5) chiede di comunicare alla società di trasporto valori.
// ---------------------------------------------------------------------------

export interface OperatoreVersamento {
  id: string;
  sede_id: string;
  nome: string;
  cognome: string;
  note: string | null;
  created_at: string;
}

export async function listOperatoriVersamento(sedeId: string): Promise<{ operatori: OperatoreVersamento[] }> {
  const { data } = await apiClient.get<{ operatori: OperatoreVersamento[] }>("/operatori-versamento", {
    params: { sede_id: sedeId },
  });
  return data;
}

export async function createOperatoreVersamento(payload: {
  sede_id: string;
  nome: string;
  cognome: string;
  note?: string;
}): Promise<{ operatore: OperatoreVersamento }> {
  const { data } = await apiClient.post<{ operatore: OperatoreVersamento }>("/operatori-versamento", payload);
  return data;
}

export async function updateOperatoreVersamento(
  id: string,
  payload: { nome?: string; cognome?: string; note?: string }
): Promise<{ operatore: OperatoreVersamento }> {
  const { data } = await apiClient.patch<{ operatore: OperatoreVersamento }>(`/operatori-versamento/${id}`, payload);
  return data;
}

export async function deleteOperatoreVersamento(id: string): Promise<{ success: true }> {
  const { data } = await apiClient.delete<{ success: true }>(`/operatori-versamento/${id}`);
  return data;
}
